<template>
  <div id="app">
    <!-- Include the header component -->
    <HeaderComponent 
      @show-deck-builder="showDeckBuilderComponent"
      @show-print="showPrintComponent"
      @show-rules="showRulesComponent"  
      @show-abilities="showAbilitiesComponent"
    />
    <!-- Main content -->
    <DeckBuilderComponent v-if="showDeckBuilder" :refresh-key="refreshKey" />
    <PrintComponent v-if="showPrint" />
    <RulesComponent v-if="showRules" />
    <!-- Ability editor only visible in admin mode -->
    <AbilityComponent v-if="showAbilities" :is-admin="isAdmin" />
  </div>
</template>

<script>
import { provide, ref } from "vue";
import HeaderComponent from "./components/HeaderComponent.vue";
import DeckBuilderComponent from "./components/DeckBuilderComponent.vue";
import PrintComponent from "./components/PrintComponent.vue";
import RulesComponent from "./components/RulesComponent.vue";
import AbilityComponent from "./components/AbilityComponent.vue";
import { getDeckBox, saveDeckBox, getDeckByName } from "./services/DeckService";
import { GetCardForSave } from "./services/CardAdapterService";

export default {
  name: "App",
  components: {
    HeaderComponent,
    DeckBuilderComponent,
    PrintComponent,
    RulesComponent,
    AbilityComponent,
  },
  setup() {
    // Admin mode flag
    const isAdmin = ref(false);

    // Load deck box
    const deckBox = ref(getDeckBox());
    const selectedDeck = ref(null);
    const refreshKey = ref(0);

    // View flags
    const showDeckBuilder = ref(false);
    const showPrint = ref(false);
    const showRules = ref(false);
    const showAbilities = ref(true);

    // Persist edits back into localStorage
    const updateSelectedDeck = () => {
      if (!selectedDeck.value) return;
      const idx = deckBox.value.findIndex(d => d.name === selectedDeck.value.name);
      if (idx !== -1) {
        deckBox.value[idx] = selectedDeck.value;
      }
    };

    // Expose global state & actions
    provide("isAdmin", isAdmin);
    provide("deckBox", deckBox);
    provide("selectedDeck", selectedDeck);

    provide("saveDeckBox", () => {
      updateSelectedDeck();
      const minimalDecks = deckBox.value.map(deck => ({
        ...deck,
        cards: deck.cards.map(c => GetCardForSave(c))
      }));
      saveDeckBox(minimalDecks);
    });

    provide("selectDeck", (deckName) => {
      const deck = getDeckByName(deckName);
      if (deck) {
        selectedDeck.value = deck;
        // Route to Abilities
        showDeckBuilder.value = false;
        showPrint.value = false;
        showRules.value = false;
        showAbilities.value = true;
      }
    });

    provide("deselectDeck", () => {
      selectedDeck.value = null;
    });

    // Manual view-switchers
    const showDeckBuilderComponent = () => {
      showDeckBuilder.value = true;
      showPrint.value = false;
      showRules.value = false;
      showAbilities.value = false;
      refreshKey.value++;
    };
    const showPrintComponent = () => {
      showDeckBuilder.value = false;
      showPrint.value = true;
      showRules.value = false;
      showAbilities.value = false;
    };
    const showRulesComponent = () => {
      showDeckBuilder.value = false;
      showPrint.value = false;
      showRules.value = true;
      showAbilities.value = false;
    };
    const showAbilitiesComponent = () => {
      showDeckBuilder.value = false;
      showPrint.value = false;
      showRules.value = false;
      showAbilities.value = true;
    };

    return {
      isAdmin,
      showDeckBuilder,
      showPrint,
      showRules,
      showAbilities,
      showDeckBuilderComponent,
      showPrintComponent,
      showRulesComponent,
      showAbilitiesComponent,
      refreshKey,
    };
  },
};
</script>
